import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, XCircle, AlertTriangle, Camera, RotateCcw } from "lucide-react";
import api from "@/lib/api";

const REGION = "qr-scan-region";

export default function Scanner() {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const busyRef = useRef(false);

  const stop = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (e) {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleDecoded = async (text) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const { data } = await api.post("/tickets/scan", { qr_token: text });
      setResult(data);
    } catch (e) {
      setResult({ result: "invalid", message: "Scan failed" });
    }
    await stop();
    setTimeout(() => (busyRef.current = false), 800);
  };

  const start = async () => {
    setResult(null);
    setError("");
    try {
      const html5 = new Html5Qrcode(REGION);
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        handleDecoded,
        () => {}
      );
      setScanning(true);
    } catch (e) {
      setError("Camera unavailable. Grant permission or use manual entry below.");
    }
  };

  useEffect(() => () => { stop(); }, []);

  const [manual, setManual] = useState("");

  const styleMap = {
    valid: { icon: CheckCircle2, cls: "text-green-600 bg-green-50 border-green-200" },
    already_used: { icon: AlertTriangle, cls: "text-amber-600 bg-amber-50 border-amber-200" },
    invalid: { icon: XCircle, cls: "text-red-600 bg-red-50 border-red-200" },
  };

  return (
    <div className="max-w-md">
      <div id={REGION} className="w-full aspect-square bg-brand-ink/5 border border-brand-ink/10 overflow-hidden [&_video]:object-cover" />

      <div className="flex gap-3 mt-4">
        {!scanning ? (
          <button onClick={start} data-testid="scanner-start" className="flex-1 rounded-full bg-brand-orange text-white py-3 font-mono text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 hover:bg-brand-orangeHover transition-colors">
            <Camera size={16} /> Start scanning
          </button>
        ) : (
          <button onClick={stop} data-testid="scanner-stop" className="flex-1 rounded-full bg-brand-ink text-white py-3 font-mono text-xs uppercase tracking-[0.2em]">
            Stop
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm mt-3">{error}</p>}

      <div className="mt-5 flex gap-2">
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          placeholder="Or paste ticket code"
          className="flex-1 border-b border-brand-ink/20 py-2 bg-transparent outline-none focus:border-brand-orange text-sm font-mono"
          data-testid="scanner-manual-input"
        />
        <button onClick={() => manual && handleDecoded(manual.trim())} data-testid="scanner-manual-check" className="text-xs font-mono uppercase tracking-widest text-brand-orange">
          Check
        </button>
      </div>

      {result && (
        (() => {
          const s = styleMap[result.result] || styleMap.invalid;
          const Icon = s.icon;
          return (
            <div className={`mt-6 border p-5 ${s.cls}`} data-testid="scan-result">
              <div className="flex items-center gap-2 font-head font-bold text-lg uppercase">
                <Icon size={22} /> {result.message}
              </div>
              {result.ticket && (
                <div className="mt-3 text-sm text-brand-ink/70">
                  <div className="font-bold text-brand-ink">{result.ticket.buyer_name}</div>
                  <div>{result.event?.title} — {result.event?.date}</div>
                  <div className="font-mono text-[11px] mt-1">{result.ticket.qr_token}</div>
                </div>
              )}
              <button onClick={start} className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-brand-ink">
                <RotateCcw size={14} /> Scan next
              </button>
            </div>
          );
        })()
      )}
    </div>
  );
}
